<script setup lang="ts">
import { computed } from 'vue'
import { useGameStore } from '@/stores/gameStore'
import { usePlanetStore } from '@/stores/planetStore'
import { useOreConversionStore } from '@/stores/oreConversionStore'
import { useAssetStore } from '@/stores/assetStore'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import HeaderFixedWidthIcon from '@/components/Interface/DataTable/Headers/HeaderFixedWidthIcon.vue'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import SimpleProgressBar from '@/components/Elements/ProgressBar/SimpleProgressBar.vue'
import PlanetIcon from '@/components/Objects/Icons/PlanetIcon.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import type PlayerPlanet from '@/types/PlayerPlanet'
import LargeWideModel from '@/components/Interface/Modal/ModelSizes/LargeWideModel.vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import { usePlayerOreStore } from '@/stores/playerOreStore'
import { CDN } from '@/models/cdn.ts'
import ToggleSwitch from '@/components/Elements/ToggleSwitch/ToggleSwitch.vue'
import Backend from '@/models/backend'
import { logger } from '@/models/logger'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const gameStore = useGameStore()
const planetStore = usePlanetStore()
const oreConversionStore = useOreConversionStore()
const assetStore = useAssetStore()
const playerOreStore = usePlayerOreStore()
const backend = new Backend()

const terraformingPlanets = computed(() => {
  const planets = new Map<number, PlayerPlanet>()
  
  planetStore.planets.forEach((planet: PlayerPlanet, id: number) => {
    // Filter out ringworlds (type 8) and planets already matching player's home world type
    if (planet.buildings.includes('terraforming_command_center') && 
        Number(planet.type) != gameStore.player?.homeWorldPlanetType &&
        Number(planet.type) !== 8) {
      planets.set(id, planet)
    }
  })
  
  return planets
})

const terraformOre = computed(() => {
  const data = oreConversionStore.getLevel2OreVisualDataFromPlanetType(Number(gameStore.player!.homeWorldPlanetType))
  return assetStore.findOreById(data.ore)
})

const calculateOreLeft = (planet: PlayerPlanet) => {
  return 25 - planet.terraformProgress
}

const totalOreRequired = computed(() => {
  let total = 0
  terraformingPlanets.value.forEach((planet) => {
    total += calculateOreLeft(planet)
  })
  return total
})

const currentOreAmount = computed(() => {
  if (!terraformOre.value) return 0
  
  // Get ore name directly from the terraformOre object and convert to lowercase
  const oreName = terraformOre.value.oreName?.toLowerCase()
  
  // Check if the ore name exists in playerOreStore data
  if (oreName && oreName in playerOreStore.data) {
    return playerOreStore.data[oreName as keyof typeof playerOreStore.data]
  }
  
  return 0
})

// Get the player's home world planet type name
const homeWorldPlanetTypeName = computed(() => {
  const planetTypeId = gameStore.player?.homeWorldPlanetType
  if (!planetTypeId) return 'Planets'
  const planetType = assetStore.findPlanetTypeById(planetTypeId)
  return planetType?.name || 'Planets'
})

// Simple filter function that returns all planets as an array
const filters = (planets: PlayerPlanet[]) => {
  return planets
}

// Toggle handler for terraforming
const handleTerraformingToggle = async (planetId: number) => {
  try {
    await backend.toggleTerraforming([planetId])
  } catch (error) {
    logger.error('Failed to toggle terraforming:', error)
  }
}
</script>

<template>
  <LargeWideModel :title="t('modals.terraforming.title')">
    <StandardCard top-padding="2" bottom-padding="2" class="px-2">
      <div class="flex flex-col sm:flex-row text-center sm:text-left">
        <img
          class="m-2 self-center sm:self-start w-auto md:w-24"
          :src="CDN.techIcon('terraforming')"
          alt="Terraforming Technology"
        />
        <div class="text-subtext p-2">
          <div class="pb-2">
            <p class="pb-1">{{ t('modals.terraforming.descriptionPart1') }} <span class="text-cyan-500">{{ t('modals.terraforming.lush') }}</span> {{ t('modals.terraforming.descriptionPart2') }} <span class="text-green-500">{{ t('modals.terraforming.paradise') }}</span> {{ t('modals.terraforming.descriptionPart3') }}</p>
            <ul class="text-slate-300 list-disc list-inside">
              <li>{{ t('modals.terraforming.requirements.building') }} <span class="text-subtext">{{ t('modals.terraforming.requirements.buildingNote') }}</span></li>
              <li>{{ t('modals.terraforming.requirements.cannotTerraform', { planetType: homeWorldPlanetTypeName }) }}</li>
              <li>{{ t('modals.terraforming.requirements.oreRequiredPart1') }} 25 <GenericIcon v-if="terraformOre" :type="'ore' + terraformOre.id" :tooltip="terraformOre.oreName" /> {{ terraformOre?.oreName || 'ore' }} {{ t('modals.terraforming.requirements.oreRequiredPart2') }} <span class="text-subtext">{{ t('modals.terraforming.requirements.oreNote') }}</span></li>
            </ul>
          </div>
          <div v-if="terraformOre">
            {{ t('modals.terraforming.oreStatusPart1') }} <span class="text-slate-200">{{ totalOreRequired.toLocaleString() }}</span> <GenericIcon :type="'ore' + terraformOre.id" :tooltip="terraformOre.oreName" /> {{ terraformOre.oreName }}, {{ t('modals.terraforming.oreStatusPart2') }} <span class="text-slate-200">{{ currentOreAmount.toLocaleString() }}</span> {{ t('modals.terraforming.oreStatusPart3') }} <span class="text-slate-200">25</span> <GenericIcon :type="'ore' + terraformOre.id" :tooltip="terraformOre.oreName" /> {{ terraformOre.oreName }} {{ t('modals.terraforming.oreStatusPart4') }}
          </div>
          <div v-else>
            {{ t('modals.terraforming.noOre') }}
          </div>
        </div>
      </div>
    </StandardCard>

    <FerionDataTable
      :data="terraformingPlanets"
      :filters="filters"
      tableStoreName="terraformingPlanets"
      :footerBar="true"
      :heightLimit="'h-[calc(100vh-25rem)]'"
      class="m-2 pb-2"
    >
      
      <template #columns="{ sort, sortColumn, sortDirection }">
        <HeaderTextFlex
          min-width="12"
          column="planetName"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          class="pl-2"
        >
          {{ t('modals.terraforming.columns.planetName') }}
        </HeaderTextFlex>
        <HeaderTextFlex
          min-width="12"
          column="starName"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
        >
          {{ t('modals.terraforming.columns.starName') }}
        </HeaderTextFlex>
        <HeaderFixedWidthIcon 
          width="4" 
          column="type" 
          :sortColumn="sortColumn" 
          :sortDirection="sortDirection" 
          @sort="sort"
        >
          <div class="flex justify-center">
            <PlanetIcon :size="2" :type="5" />
          </div>
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthIcon 
          width="6" 
          column="population" 
          :sortColumn="sortColumn" 
          :sortDirection="sortDirection" 
          @sort="sort"
        >
          <GenericIcon :tooltip="t('modals.terraforming.tooltips.population')" type="person" />
        </HeaderFixedWidthIcon>
        <HeaderFixedWidthText
          width="15"
          column="terraformProgress"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="left"
        >
          {{ t('modals.terraforming.columns.progress') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          class="rounded-tr pr-2"
          width="8"
          column="terraformingEnabled"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('modals.terraforming.columns.enabled') }}
        </HeaderFixedWidthText>
      </template>
      
      <template #rows="{ item }">
        <CellFlexWidth min-width="12" class="pl-2">
          <div class="text-slate-300">{{ item.planetName }}</div>
        </CellFlexWidth>
        
        <CellFlexWidth min-width="12">
          <div class="text-slate-300">{{ item.starName }}</div>
        </CellFlexWidth>
        
        <CellFixedWidth width="4">
          <PlanetIcon :type="Number(item.type)" :size="item.size" />
        </CellFixedWidth>
        
        <CellFixedWidth width="6" class="text-slate-300">
          {{ item.population }}
        </CellFixedWidth>
        
        <CellFixedWidth width="15" align="left">
          <SimpleProgressBar 
            v-if="item.terraformingEnabled > 0"
            :percentage="item.terraformProgress / 25 * 100"
            :text="`${Math.round(item.terraformProgress / 25 * 100)}%`"
            :subtext="t('modals.terraforming.oreLeft', { count: calculateOreLeft(item), ore: terraformOre?.oreName })"
            color="blue"
            :height="20"
          />
          <span v-else class="text-gray-500">{{ t('modals.terraforming.disabled') }}</span>
        </CellFixedWidth>
        
        <CellFixedWidth width="8" align="center" class="pr-2">
          <ToggleSwitch 
            :modelValue="item.terraformingEnabled > 0"
            @update:modelValue="handleTerraformingToggle(item.id)"
          />
        </CellFixedWidth>
      </template>
    </FerionDataTable>
  </LargeWideModel>
</template>

<style scoped lang="scss">
</style>